import { computed, ref } from 'vue'
import { getInsertRejectMessage, type ValidateEventInsertResult } from '@/events/list/eventListOperations'
import type { DropdownOption, MessageApi } from 'naive-ui'

type SceneEventSelectionPayload = {
  index: number
  append: boolean
  range: boolean
}

type AddPlaceholderSelectionPayload = {
  key: string
  insertAt: number
  previousIndex: number
}

type EventMovePayload = {
  targetIndex: number
  position: 'before' | 'after'
}

type EventContextMenuPayload = {
  x: number
  y: number
  index?: number
  addPlaceholder?: AddPlaceholderSelectionPayload
}

type EventAction =
  | { type: 'copy' }
  | { type: 'cut' }
  | { type: 'paste' }
  | { type: 'delete' }
  | { type: 'move'; targetIndex: number; position: 'before' | 'after' }

type SceneEditorActionBridge = {
  hasEventClipboard: boolean
  selectedAddPlaceholder: AddPlaceholderSelectionPayload | null
  canPasteAtSelection: () => boolean
  validatePasteAtSelection: () => ValidateEventInsertResult
  copySelectedEvents: () => boolean
  cutSelectedEvents: () => boolean
  deleteSelectedEvents: () => boolean
  moveSelectedEvents: (targetIndex: number, position: 'before' | 'after') => boolean
  pasteClipboardEvents: () => boolean
  selectAddPlaceholder: (payload: AddPlaceholderSelectionPayload) => void
  selectEventWithModifiers: (
    index: number,
    options: { append: boolean; range: boolean },
  ) => void
}

const EVENT_CONTEXT_MENU_WIDTH = 132
const EVENT_CONTEXT_MENU_ITEM_HEIGHT = 34
const EVENT_CONTEXT_MENU_PADDING = 16
const EVENT_CONTEXT_MENU_SAFE_GAP = 72

const FULL_EVENT_MENU_OPTIONS: DropdownOption[] = [
  { label: '复制', key: 'copy' },
  { label: '剪切', key: 'cut' },
  { label: '删除', key: 'delete' },
  { label: '粘贴', key: 'paste' },
]

function clampContextMenuPosition(
  x: number,
  y: number,
  itemCount: number,
): { x: number; y: number } {
  if (typeof window === 'undefined') return { x, y }
  const viewport = window.visualViewport
  const width = Math.round(viewport?.width ?? window.innerWidth)
  const height = Math.round(viewport?.height ?? window.innerHeight)
  const menuHeight = itemCount * EVENT_CONTEXT_MENU_ITEM_HEIGHT + EVENT_CONTEXT_MENU_PADDING
  const minX = Math.min(width - 8, EVENT_CONTEXT_MENU_WIDTH + 8)
  const maxX = Math.max(minX, width - 8)
  const maxY = Math.max(8, height - menuHeight - EVENT_CONTEXT_MENU_SAFE_GAP)
  return {
    x: Math.min(Math.max(minX, x), maxX),
    y: Math.min(Math.max(8, y), maxY),
  }
}

/**
 * 管理工程编辑器中的事件选择、右键菜单与剪贴板操作。
 *
 * @param editor 场景编辑器能力桥，提供事件选择与增删改移动方法。
 * @param message Naive UI 的消息实例，用于反馈失败原因。
 * @returns 供视图层绑定的菜单状态、事件处理函数与快捷操作入口。
 */
export function useProjectViewerEventActions(
  editor: SceneEditorActionBridge,
  message: MessageApi,
) {
  const eventCtxShow = ref(false)
  const eventCtxX = ref(0)
  const eventCtxY = ref(0)

  const eventCtxOptions = computed<DropdownOption[]>(() => {
    const pasteDisabled = !editor.hasEventClipboard || !editor.canPasteAtSelection()
    if (editor.selectedAddPlaceholder) {
      return [{ label: '粘贴', key: 'paste', disabled: pasteDisabled }]
    }
    return FULL_EVENT_MENU_OPTIONS.map((item) => (
      item.key === 'paste'
        ? { ...item, disabled: pasteDisabled }
        : item
    ))
  })

  function closeEventContextMenu(): void {
    eventCtxShow.value = false
  }

  /**
   * 执行一条事件列表操作，并在成功或失败时给出统一反馈。
   *
   * @param action 当前要执行的事件操作。
   * @returns 是否执行成功。
   */
  function executeEventAction(action: EventAction): boolean {
    switch (action.type) {
      case 'copy':
        if (!editor.copySelectedEvents()) return false
        return true
      case 'cut':
        if (!editor.cutSelectedEvents()) return false
        return true
      case 'paste': {
        if (editor.pasteClipboardEvents()) return true
        const validated = editor.validatePasteAtSelection()
        message.warning(getInsertRejectMessage(validated.ok ? 'empty' : validated.reason))
        return false
      }
      case 'delete':
        if (!editor.deleteSelectedEvents()) return false
        return true
      case 'move':
        if (!editor.moveSelectedEvents(action.targetIndex, action.position)) {
          message.warning('无法移动事件到此处')
          return false
        }
        return true
    }
  }

  /**
   * 处理事件列表中的选择行为，支持单选、多选和区间选择。
   *
   * @param payload 事件列表组件回传的选中信息。
   * @returns 无返回值。
   */
  function onSceneEventSelect(payload: SceneEventSelectionPayload): void {
    editor.selectEventWithModifiers(payload.index, {
      append: payload.append,
      range: payload.range,
    })
  }

  /**
   * 处理空插槽选择，记录后续插入事件的位置。
   *
   * @param payload 插入占位信息。
   * @returns 无返回值。
   */
  function onSceneAddPlaceholderSelect(payload: AddPlaceholderSelectionPayload): void {
    editor.selectAddPlaceholder(payload)
  }

  /**
   * 打开事件右键菜单，并记录弹出位置。
   *
   * @param payload 菜单弹出坐标与当前事件上下文。
   * @returns 无返回值。
   */
  function openEventContextMenu(payload: EventContextMenuPayload): void {
    if (payload.addPlaceholder) {
      editor.selectAddPlaceholder(payload.addPlaceholder)
    }
    const position = clampContextMenuPosition(payload.x, payload.y, eventCtxOptions.value.length)
    eventCtxX.value = position.x
    eventCtxY.value = position.y
    eventCtxShow.value = true
  }

  /**
   * 处理右键菜单点击结果，并转成统一事件操作。
   *
   * @param key 被点击菜单项的标识。
   * @returns 无返回值。
   */
  function onEventCtxSelect(key: string | number): void {
    closeEventContextMenu()
    switch (key) {
      case 'copy':
        executeEventAction({ type: 'copy' })
        break
      case 'cut':
        executeEventAction({ type: 'cut' })
        break
      case 'delete':
        executeEventAction({ type: 'delete' })
        break
      case 'paste':
        executeEventAction({ type: 'paste' })
        break
    }
  }

  /**
   * 处理事件拖拽排序结果。
   *
   * @param payload 目标位置与插入方向。
   * @returns 无返回值。
   */
  function onEventMove(payload: EventMovePayload): void {
    executeEventAction({
      type: 'move',
      targetIndex: payload.targetIndex,
      position: payload.position,
    })
  }

  return {
    eventCtxOptions,
    eventCtxShow,
    eventCtxX,
    eventCtxY,
    closeEventContextMenu,
    executeEventAction,
    onEventCtxSelect,
    onEventMove,
    onSceneAddPlaceholderSelect,
    onSceneEventSelect,
    openEventContextMenu,
  }
}
